// Startup helpers for main.ts, kept in their own module so they can be tested without running
// the whole site init (main.ts initializes on import).

const COMPONENTS_CSS_SELECTOR = "link[data-components-css]";

// Upper bound on waiting for components.css: past this, blocks initialize anyway (worst case
// they measure an unstyled layout) rather than never.
export const COMPONENTS_CSS_TIMEOUT_MS = 3000;

// components.css loads without blocking rendering (Layout.cshtml: rel="preload" that switches
// itself to rel="stylesheet" on load), so it may not be applied yet when this module runs.
// Resolves once it is - or after timeoutMs regardless (Infinity: only once it is, or failed to
// load; for work that must not run against the unstyled layout, like scrolling to an anchor).
export function whenComponentsCssApplied(timeoutMs = COMPONENTS_CSS_TIMEOUT_MS): Promise<void> {
    const link = document.querySelector<HTMLLinkElement>(COMPONENTS_CSS_SELECTOR);
    const isApplied = (): boolean => !link || (link.rel === "stylesheet" && link.sheet !== null);
    if (isApplied()) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const done = (): void => {
            link!.removeEventListener("load", onLoad);
            link!.removeEventListener("error", done);
            clearTimeout(timeout);
            resolve();
        };
        // Fires once for the preload, then again once the swapped-in stylesheet is applied.
        const onLoad = (): void => {
            if (isApplied()) {
                done();
            }
        };
        const timeout = Number.isFinite(timeoutMs) ? setTimeout(done, timeoutMs) : undefined;
        link!.addEventListener("load", onLoad);
        // Failed to load (404, network): don't keep every block waiting for the timeout.
        link!.addEventListener("error", done);
    });
}

// One component failing must never stop the others from initializing.
export function safeInit(inits: Array<() => void>): void {
    for (const initComponent of inits) {
        try {
            initComponent();
        } catch (error) {
            console.warn("Component failed to initialize", error);
        }
    }
}
